import { InfoCircleOutlined } from '@ant-design/icons'
import { DatePicker } from 'antd'
import moment from 'moment'
import { useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { Checkbox } from 'sutra/components/Checkbox/Checkbox'
import { CheckboxGroup } from 'sutra/components/Checkbox/CheckboxGroup'
import { FormItem } from 'sutra/components/Form'
import { Input } from 'sutra/components/Input/Input'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { type SourceProps } from 'features/project-form/features/source/components/source-providers-utils'
import {
  amazonDomains,
  amazonOptions,
  getAmazonDomain,
} from 'features/project-form/features/source/components/sources/AmazonOptions'
import { AmazonFilterSelect } from 'features/project-form/features/source/components/sources/AmazonUtils'
import {
  extractUrl,
  retrievePureUrl,
} from 'features/project-form/features/source/utils'

export const Amazon = ({ formik }: SourceProps): JSX.Element => {
  const sourceUrl = getAmazonDomain(formik.values.sourceUrl || '')
  const sourceUrlValue = useMemo(
    () => extractUrl(formik.values.sourceUrl, sourceUrl),
    [formik.values.sourceUrl, sourceUrl],
  )

  useEffect(() => {
    if (formik.values.subtype === undefined)
      formik.setFieldValue('subtype', ['reviews'])
    if (formik.values.global_reviews === undefined)
      formik.setFieldValue('global_reviews', false)
  }, [formik])

  const amazonFilterOptions = (
    <AmazonFilterSelect
      value={sourceUrl}
      onChange={(option) => {
        if (formik.values.sourceUrl) {
          formik.setFieldValue(
            'sourceUrl',
            formik.values.sourceUrl.replace(sourceUrl, option),
          )
        } else {
          formik.setFieldValue('sourceUrl', option)
        }
      }}
      options={amazonOptions}
    />
  )

  return (
    <>
      <FormItem
        label={
          <Tooltip content="Provide the URL of the page you want pulled.">
            <span>
              URL <InfoCircleOutlined />
            </span>
          </Tooltip>
        }
        validateStatus={formik.errors.sourceUrl ? 'error' : ''}
        help={formik.errors.sourceUrl}
        wrapperCol={{ span: 16 }}
      >
        <DomainRow>
          {amazonFilterOptions}
          <Input
            name="sourceUrl"
            value={sourceUrlValue}
            $error={Boolean(formik.errors.sourceUrl)}
            onChange={(event) => {
              if (!event.target.value) {
                // If the input is empty, set Amazon domain as sourceUrl.
                // As we have additional validation for Amazon,
                // it will prevent passing pure domain as the source link.
                formik.setFieldValue('sourceUrl', sourceUrl)
              } else {
                const targetDomain = retrievePureUrl(event.target.value)
                const amazonDomain = amazonDomains.find(
                  (country_domain) =>
                    targetDomain.indexOf(retrievePureUrl(country_domain)) !==
                    -1,
                )
                formik.setFieldValue(
                  'sourceUrl',
                  amazonDomain
                    ? amazonDomain +
                        targetDomain.replace(retrievePureUrl(amazonDomain), '')
                    : sourceUrl + event.target.value,
                )
              }
            }}
          />
        </DomainRow>
      </FormItem>
      <FormItem
        label={
          <Tooltip content="Choose data types to process.">
            <span>
              Data types <InfoCircleOutlined />
            </span>
          </Tooltip>
        }
        validateStatus={formik.errors.subtype ? 'error' : ''}
        help={formik.errors.subtype}
        wrapperCol={{ span: 16 }}
      >
        <SubtypeGroup
          aria-label="Data types"
          value={formik.values.subtype ?? []}
          onValueChange={(values) => {
            formik.setFieldValue('subtype', values)
          }}
        >
          <Checkbox value="reviews">Reviews</Checkbox>
          <Checkbox value="questions">Questions</Checkbox>
        </SubtypeGroup>
      </FormItem>

      <FormItem
        label={
          <Tooltip content="Check this to pull reviews that were submitted outside of the specified Amazon domain.">
            <span>
              Global Reviews <InfoCircleOutlined />
            </span>
          </Tooltip>
        }
        wrapperCol={{ span: 16 }}
      >
        <Checkbox
          name="global_reviews"
          disabled={!formik.values.subtype?.includes('reviews')}
          checked={formik.values.global_reviews}
          onChange={(checked) =>
            formik.setFieldValue('global_reviews', checked)
          }
        />
      </FormItem>
      <FormItem
        label={
          <Tooltip content="Give your source a name to display in the charts. Make sure it's one you'll recognize.">
            <span>
              Source Title <InfoCircleOutlined />
            </span>
          </Tooltip>
        }
        validateStatus={formik.errors.sourceTitle ? 'error' : ''}
        help={formik.errors.sourceTitle}
        wrapperCol={{ span: 10 }}
      >
        <Input
          name="sourceTitle"
          onChange={formik.handleChange}
          value={formik.values.sourceTitle}
          $error={Boolean(formik.errors.sourceTitle)}
          placeholder="Insert title here"
        />
      </FormItem>
      <FormItem
        label={
          <Tooltip content="Select the earliest date from which data should be pulled.">
            <span>
              Date <InfoCircleOutlined />
            </span>
          </Tooltip>
        }
        wrapperCol={{ span: 16 }}
        validateStatus={formik.errors.sourceDate ? 'error' : ''}
        help={formik.errors.sourceDate}
      >
        <DatePicker
          defaultValue={
            formik.values.sourceDate && moment(formik.values.sourceDate)
          }
          onChange={(value) => {
            if (value) {
              formik.setFieldValue('sourceDate', value.toDate())
            }
          }}
          format={'MM/DD/YYYY'}
        />
      </FormItem>
    </>
  )
}

// Replaces antd's Input `addonBefore`: the domain Select sits beside the Input.
const DomainRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const SubtypeGroup = styled(CheckboxGroup)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
`
